import { Form, FormTextInput, StandardFormButtons } from '@components/form';
import { useEditSecretMutation, type Secret } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  value: Yup.string(),
});

type EditSecretModalProps = {
  organizationId?: string;
  workspaceId?: string;
  secret: Secret;
  onEdit?: VoidFunction;
  onClose?: VoidFunction;
};

export const EditSecretModal = ({
  organizationId,
  workspaceId,
  secret,
  onEdit,
  onClose,
}: EditSecretModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const [editSecretMutation] = useEditSecretMutation();

  const secretType = workspaceId ? 'workspace' : organizationId ? 'organization' : 'user';

  const editSecret = async ({ value }: { value: string }) => {
    posthog.capture('agent-studio:user_edit-secret_submitted', {
      secretId: secret.secretId,
    });
    await editSecretMutation({
      variables: {
        secretId: secret.secretId,
        value: value || undefined,
        includeInAllWorkspaces: secretType === 'user' ? undefined : secretType === 'organization',
        workspaceIds: secretType === 'workspace' ? secret.workspaceIds : undefined,
      },
      onCompleted: () => {
        posthog.capture('agent-studio:user_edit-secret_completed', {
          secretId: secret.secretId,
        });
        onEdit?.();
        onClose?.();
      },
      onError: (error) => {
        posthog.capture('agent-studio:user_edit-secret_failed', {
          secretId: secret.secretId,
          error: (error as Error)?.message || 'An error occurred during edit',
        });
        throw error;
      },
    });
  };

  const handleSubmit = createAsyncNotification(
    `${secretType.charAt(0).toUpperCase()}${secretType.slice(1)} secret updated successfully.`,
    editSecret,
    {
      loadingMessage: `Updating ${secretType} secret...`,
    },
  );

  return (
    <Form
      enableReinitialize
      initialValues={{
        value: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Stack spacing={4}>
          <FormTextInput
            name="value"
            label="New secret value"
            placeholder="Leave blank to keep current value"
            type="password"
            autoComplete="off"
            multiline
            rows={3}
          />
          <StandardFormButtons onClose={onClose} submitLabel="Update secret" />
        </Stack>
      )}
    </Form>
  );
};
